import { defineStore } from 'pinia';
import { computed, ref } from 'vue-demi';
import jwtDecode from 'jwt-decode';
import type { AccessTokenPayload } from '@ilteducation/auth';
import { tokensByRefreshToken } from './api';
import { load, persist, remove } from './storage';
import { checkIsOnline } from './network';
import type { Tokens } from './domain';

const STORAGE_KEY_TOKENS = 'ilt-auth.tokens';

type State = {
  accessToken: string;
  clientId?: string;
  idToken?: string;
  refreshToken?: string;
};

/**
 * Check if the token is valid long enough to be used for one more request.
 *
 */
export const isFreshEnough = (tokens: Tokens): boolean => {
  const { exp: tokenExpiryTimestampInSeconds } = jwtDecode(tokens.accessToken) as AccessTokenPayload;
  const nowTimestampInSeconds = Date.now() / 1000;

  /*
  This assumes that the token is initially valid for at least this amount of  seconds.
  Otherwise, we get into a token refreshing loop, because a new token is already considered stale.
   */
  const maxTimeNeededForARequestInSeconds = 60 * 5;

  return tokenExpiryTimestampInSeconds - nowTimestampInSeconds >= maxTimeNeededForARequestInSeconds;
};

/*
We want to allow only one request to get the valid tokens at a time.

This is because we don't want to refresh the token multiple times when multiple requests are made at the same time.
 */
let getValidTokensPromise: Promise<Tokens | null> | null = null;

const useTokens = defineStore('ilt-auth-tokens', () => {
  const state = ref<State>();
  const isInitialised = ref(false);

  const acceptTokens = async (tokens: Tokens) => {
    await persist(STORAGE_KEY_TOKENS, tokens);

    let clientId;

    const { refreshToken } = tokens;
    if (refreshToken) {
      const { aud } = jwtDecode(tokens.accessToken) as AccessTokenPayload;

      clientId = aud;
    }

    state.value = { ...tokens, clientId };
  };

  const clearTokens = async () => {
    await remove(STORAGE_KEY_TOKENS);

    state.value = undefined;
  };

  const refreshTokens = async (tokens: Tokens, clientId: string): Promise<void> => {
    const isOnline = await checkIsOnline();
    if (!isOnline) {
      // simply pass the old tokens
      console.log("App is offline, so we're using the old STUFF THAT GIVES US ACCESS"); // Avoid using "token" word in logs since they are filtered by sentry
      return acceptTokens(tokens);
    }

    return tokensByRefreshToken({
      refreshToken: tokens.refreshToken!,
      clientId,
    })
      .then(acceptTokens)
      .catch(clearTokens);
  };

  const forceTokensRefresh = async () =>
    load<Tokens>(STORAGE_KEY_TOKENS).then((persistedTokens) => {
      if (!persistedTokens) {
        throw new Error('No persisted STUFF available'); // Avoid using "token" word in logs since they are filtered by sentry
      }

      const { aud } = jwtDecode(persistedTokens.accessToken) as AccessTokenPayload;
      return refreshTokens(persistedTokens, aud);
    });

  const tokens = computed(() =>
    state.value ? { idToken: state.value.idToken, accessToken: state.value.accessToken } : null,
  );

  // Load persisted tokens on initial load
  load<Tokens>(STORAGE_KEY_TOKENS).then((persistedTokens) => {
    if (!persistedTokens) {
      isInitialised.value = true;
      return;
    }

    acceptTokens(persistedTokens).finally(() => {
      isInitialised.value = true;
    });
  });

  // TODO - document that in case we are offline, we return the old tokens
  const refreshOldToken = async (oldTokens: Tokens): Promise<Tokens> => {
    const isOnline = await checkIsOnline();
    if (!isOnline) {
      console.log('App is offline, so we can not refresh the old stuff, unfortunately'); // Avoid using "token" word in logs since they are filtered by sentry

      return oldTokens;
    }
    if (!oldTokens.refreshToken) {
      throw new Error('No refresh RESOURCE available'); // Avoid using "token" word in logs since they are filtered by sentry
    }

    const { aud } = jwtDecode(oldTokens.accessToken) as AccessTokenPayload;

    const freshlySqueezedTokens = await tokensByRefreshToken({
      refreshToken: oldTokens.refreshToken,
      clientId: aud,
    });

    await acceptTokens(freshlySqueezedTokens);

    return freshlySqueezedTokens;
  };

  /**
   * Returns the current tokens if they are valid, otherwise tries to refresh them and get the fresh one 🍋
   */
  const getValidTokens = async (): Promise<Tokens | null> => {
    if (getValidTokensPromise) {
      return getValidTokensPromise;
    }

    getValidTokensPromise = new Promise<Tokens | null>((resolve) => {
      load<Tokens>(STORAGE_KEY_TOKENS)
        .then((persistedTokens) => {
          if (!persistedTokens) {
            return null;
          } else {
            // Stored token is still fine
            if (isFreshEnough(persistedTokens)) {
              return persistedTokens;
            } else {
              // Token is expired or about to expire, let's refresh it
              return refreshOldToken(persistedTokens);
            }
          }
        })
        .then((freshTokens) => {
          resolve(freshTokens);
        })
        .catch(() => {
          // We log out if we can't get the tokens
          clearTokens();
        });
    }).finally(() => {
      getValidTokensPromise = null;
    });

    return getValidTokensPromise;
  };

  return {
    isInitialised,
    tokens,
    getValidTokens,
    acceptTokens,
    clearTokens,
    forceTokensRefresh,
  };
});

export default useTokens;
